import { useState, useCallback, useEffect } from "react";
import { useSearchParams, NavLink, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { Trophy, Plus, TrendingUp, TrendingDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { EnhancedEquityCurve } from "@/components/dashboard/EnhancedEquityCurve";
import { WelcomeModal } from "@/components/onboarding/WelcomeModal";
import { EmptyStateCTA } from "@/components/dashboard/EmptyStateCTA";
import { useDashboardData } from "@/hooks/useDashboardData";
import { useRealtimeDashboard } from "@/hooks/useRealtimeDashboard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { DashboardSkeleton } from "@/components/shared/PageSkeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import { getTrades } from "@/stores/tradeStore";
import { trackSubscribe } from "@/lib/analytics";
import type { TimeRange } from "@/components/dashboard/DashboardTimeFilter";

// â"€â"€â"€ Time range config â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

const TIME_RANGES: { label: string; value: TimeRange }[] = [
  { label: "1D",  value: "today" },
  { label: "7D",  value: "week"  },
  { label: "1M",  value: "month" },
  { label: "All", value: "all"   },
];

// â"€â"€â"€ KPI Strip â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

interface KPIItem {
  label: string;
  value: string;
  mobileValue?: string;
  sentiment?: "positive" | "negative" | "neutral";
  mobileOnly?: boolean;
  desktopOnly?: boolean;
}

function KPIStrip({ items }: { items: KPIItem[] }) {
  const visibleItems = items.filter((i) => !i.desktopOnly);
  const allItems = items.filter((i) => !i.mobileOnly);
  return (
    <div
      style={{
        borderTop: "1px solid rgba(255,255,255,0.06)",
        borderLeft: "1px solid rgba(255,255,255,0.06)",
        background: "rgba(255,255,255,0.02)",
      }}
    >
      {/* Mobile: compact 2-col grid, priority items only */}
      <div className="grid grid-cols-2 lg:hidden">
        {visibleItems.map((item) => {
          const valueColor =
            item.sentiment === "positive" ? "#1d9e75" :
            item.sentiment === "negative" ? "#ef4444" :
            "rgba(255,255,255,1)";
          return (
            <div
              key={item.label}
              style={{
                padding: "10px 12px",
                display: "flex",
                flexDirection: "column",
                gap: 3,
                overflow: "hidden",
                borderRight: "1px solid rgba(255,255,255,0.06)",
                borderBottom: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <span style={{ fontSize: 8, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.08em", color: "rgba(255,255,255,0.35)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {item.label}
              </span>
              <span style={{ fontFamily: "monospace", fontSize: 15, fontWeight: 700, lineHeight: 1, color: valueColor, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {item.mobileValue ?? item.value}
              </span>
            </div>
          );
        })}
      </div>
      {/* Desktop: full row */}
      <div className="hidden lg:grid" style={{ gridTemplateColumns: `repeat(${allItems.length}, minmax(0, 1fr))` }}>
        {allItems.map((item) => {
          const valueColor =
            item.sentiment === "positive" ? "#1d9e75" :
            item.sentiment === "negative" ? "#ef4444" :
            "rgba(255,255,255,1)";
          return (
            <div
              key={item.label}
              style={{
                padding: "12px 16px",
                display: "flex",
                flexDirection: "column",
                gap: 4,
                overflow: "hidden",
                borderRight: "1px solid rgba(255,255,255,0.06)",
                borderBottom: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <span style={{ fontSize: 9, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.08em", color: "rgba(255,255,255,0.35)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {item.label}
              </span>
              <span style={{ fontFamily: "monospace", fontSize: 18, fontWeight: 700, lineHeight: 1, color: valueColor, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {item.value}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function compactDollar(v: number): string {
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 10_000)    return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${abs.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function compactPnl(v: number): string {
  const prefix = v >= 0 ? "+" : "";
  return `${prefix}${compactDollar(v)}`;
}

// â"€â"€â"€ Recent Trades Table â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€


//  Mobile Dashboard 

interface MobileDashboardProps {
  d: ReturnType<typeof useDashboardData>;
  rawTrades: RawTrade[];
  currentBalance: number;
  equityCurve: { date: string; balance: number; equity: number; drawdown: number }[];
  startingBalance: number;
  timeRange: TimeRange;
  setTimeRange: (r: TimeRange) => void;
  excludeEval: boolean;
  setExcludeEval: (fn: (v: boolean) => boolean) => void;
  activeChallenge: Record<string, unknown> | null;
}

function MobileDashboard({
  d, rawTrades, currentBalance, equityCurve, startingBalance,
  timeRange, setTimeRange, excludeEval, setExcludeEval, activeChallenge,
}: MobileDashboardProps) {
  const navigate = useNavigate();
  const todayPnl = d.todayPnl;
  const isPos = todayPnl >= 0;
  const pnlColor = isPos ? "#1d9e75" : "#ef4444";
  const recent = rawTrades.slice(0, 6);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Morning" : hour < 17 ? "Afternoon" : "Evening";

  return (
    <div className="flex flex-col gap-0 -mx-4 -mt-4 min-h-full">

      {/*  Hero: Today P&L  */}
      <div className="px-5 pt-5 pb-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <p className="text-[9px] font-mono uppercase tracking-[0.16em] mb-3" style={{ color: "rgba(255,255,255,0.25)" }}>
          Good {greeting}
        </p>

        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[9px] font-mono uppercase tracking-[0.1em] mb-1" style={{ color: "rgba(255,255,255,0.35)" }}>
              Today&apos;s P&amp;L
            </p>
            <div className="flex items-center gap-2 min-w-0">
              {isPos
                ? <TrendingUp size={18} style={{ color: pnlColor, flexShrink: 0 }} strokeWidth={1.5} />
                : <TrendingDown size={18} style={{ color: pnlColor, flexShrink: 0 }} strokeWidth={1.5} />
              }
              <span
                className="font-mono font-bold tabular-nums leading-none truncate"
                style={{ fontSize: "clamp(24px, 9vw, 40px)", color: pnlColor, letterSpacing: "-0.01em" }}
              >
                {isPos ? "+" : "-"}${Math.abs(todayPnl).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
          </div>

          {/* Time range pills */}
          <div className="flex items-center shrink-0 mt-1" style={{ border: "1px solid rgba(255,255,255,0.06)" }}>
            {TIME_RANGES.map((r) => (
              <button
                key={r.value}
                onClick={() => setTimeRange(r.value)}
                className="transition-colors"
                style={{
                  padding: "5px 9px",
                  fontSize: 9,
                  fontFamily: "monospace",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  borderRight: r.value !== "all" ? "1px solid rgba(255,255,255,0.06)" : undefined,
                  background: timeRange === r.value ? "rgba(255,255,255,0.08)" : "transparent",
                  color: timeRange === r.value ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.3)",
                }}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* Quick stat strip */}
        <div className="flex items-center gap-0 mt-4" style={{ border: "1px solid rgba(255,255,255,0.06)" }}>
          {[
            { label: "Win Rate", value: `${d.winRate}%`,            color: d.winRate >= 50 ? "#1d9e75" : "#ef4444" },
            { label: "Trades",   value: String(d.tradeCount),       color: "rgba(255,255,255,0.85)" },
            { label: "Balance",  value: compactDollar(currentBalance), color: "rgba(255,255,255,0.85)" },
            { label: "Max DD",   value: `${d.maxDD.toFixed(1)}%`,   color: d.maxDD > 10 ? "#ef4444" : "rgba(255,255,255,0.5)" },
          ].map((s, i) => (
            <div
              key={s.label}
              className="flex-1 flex flex-col gap-0.5 px-3 py-2.5"
              style={{ borderRight: i < 3 ? "1px solid rgba(255,255,255,0.06)" : undefined }}
            >
              <span className="font-mono font-bold tabular-nums text-sm" style={{ color: s.color }}>{s.value}</span>
              <span className="font-mono uppercase tracking-[0.06em]" style={{ fontSize: 8, color: "rgba(255,255,255,0.3)" }}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/*  Equity sparkline  */}
      {equityCurve.length > 1 && (
        <div className="h-[140px] overflow-hidden" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <EnhancedEquityCurve data={equityCurve} startingBalance={startingBalance} compact />
        </div>
      )}

      {/*  Primary CTA  */}
      <div className="px-4 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <button
          onClick={() => navigate("/journal", { state: { openForm: true } })}
          className="flex items-center justify-center gap-2 w-full h-12 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-black transition-opacity active:opacity-80"
          style={{ background: "#1d9e75" }}
        >
          <Plus size={15} strokeWidth={2.5} />
          Log Trade
        </button>
      </div>

      {/*  Recent trades  */}
      <div className="flex flex-col flex-1">
        <div
          className="flex items-center justify-between px-5 py-3"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.12em]" style={{ color: "rgba(255,255,255,0.35)" }}>
            Recent Trades
          </span>
          <NavLink
            to="/journal"
            className="font-mono text-[10px] uppercase tracking-[0.08em] transition-colors"
            style={{ color: "rgba(255,255,255,0.25)" }}
          >
            View all 
          </NavLink>
        </div>

        {recent.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12 px-6 text-center">
            <span className="font-mono text-[11px] uppercase tracking-[0.1em]" style={{ color: "rgba(255,255,255,0.2)" }}>
              No trades yet
            </span>
            <NavLink
              to="/journal"
              className="font-mono text-[10px] uppercase tracking-[0.1em] px-4 py-2 transition-colors"
              style={{ border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.4)" }}
            >
              Log your first trade
            </NavLink>
          </div>
        ) : (
          recent.map((t) => {
            const pnl = Number(t.pnl);
            const win = pnl >= 0;
            return (
              <div
                key={t.id}
                className="flex items-center justify-between px-5 py-3.5 transition-colors active:bg-white/[0.02]"
                style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="self-stretch"
                    style={{ width: 3, background: t.direction === "long" ? "#1d9e75" : "#ef4444", borderRadius: 2, minHeight: 32 }}
                  />
                  <div>
                    <span className="font-mono text-[13px] font-bold" style={{ color: "rgba(255,255,255,0.85)" }}>
                      {t.instrument}
                    </span>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span
                        className="font-mono text-[8px] font-bold uppercase tracking-[0.08em] px-1.5 py-0.5"
                        style={{
                          color: t.direction === "long" ? "#1d9e75" : "#ef4444",
                          background: t.direction === "long" ? "rgba(29,158,117,0.12)" : "rgba(239,68,68,0.12)",
                        }}
                      >
                        {t.direction === "long" ? "LONG" : "SHORT"}
                      </span>
                      <span className="font-mono text-[10px]" style={{ color: "rgba(255,255,255,0.25)" }}>
                        {t.date?.slice(5)}
                      </span>
                    </div>
                  </div>
                </div>
                <span
                  className="font-mono text-[15px] font-bold tabular-nums"
                  style={{ color: win ? "#1d9e75" : "#ef4444" }}
                >
                  {win ? "+" : "-"}${Math.abs(pnl).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </span>
              </div>
            );
          })
        )}
      </div>

      {/*  Active challenge banner  */}
      {activeChallenge && (
        <Link
          to="/prop-challenge"
          className="flex items-center gap-2.5 px-5 py-3 transition-opacity active:opacity-70"
          style={{ background: "rgba(29,158,117,0.06)", borderTop: "1px solid rgba(29,158,117,0.2)" }}
        >
          <Trophy size={13} strokeWidth={1.5} style={{ color: "#1d9e75", flexShrink: 0 }} />
          <span className="font-mono text-[10px] uppercase tracking-[0.08em] font-bold" style={{ color: "#1d9e75" }}>
            Prop Challenge
          </span>
          <span className="font-mono text-[10px]" style={{ color: "rgba(255,255,255,0.35)" }}>
            · {String((activeChallenge as Record<string, unknown>).firm ?? "").toUpperCase()}
          </span>
          <span className="font-mono text-[9px] ml-auto" style={{ color: "rgba(255,255,255,0.25)" }}></span>
        </Link>
      )}

      {/*  Eval toggle  */}
      <div
        className="flex items-center justify-between px-5 py-3"
        style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
      >
        <span className="font-mono text-[9px] uppercase tracking-[0.1em]" style={{ color: "rgba(255,255,255,0.25)" }}>
          {excludeEval ? "Live trades only" : "All trades"}
        </span>
        <button
          onClick={() => setExcludeEval((v) => !v)}
          className="font-mono text-[9px] uppercase tracking-[0.08em] px-2.5 py-1.5 transition-colors"
          style={{
            border: "1px solid rgba(255,255,255,0.06)",
            color: excludeEval ? "#1d9e75" : "rgba(255,255,255,0.3)",
            background: excludeEval ? "rgba(29,158,117,0.06)" : "transparent",
          }}
        >
          {excludeEval ? "Live" : "All"}
        </button>
      </div>
    </div>
  );
}


interface RawTrade {
  id: string;
  instrument: string;
  direction: string;
  pnl: number;
  date: string;
  session: string | null;
}

function RecentTradesTable({ trades }: { trades: RawTrade[] }) {
  const recent = trades.slice(0, 8);
  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <span
          className="text-[10px] font-medium uppercase tracking-[0.1em]"
          style={{ color: "rgba(255,255,255,0.4)" }}
        >
          Recent Trades
        </span>
      </div>
      {/* Mobile card list */}
      <div className="flex-1 overflow-y-auto lg:hidden">
        {recent.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <span className="text-xs uppercase tracking-[0.08em]" style={{ color: "rgba(255,255,255,0.25)" }}>
              No trades yet
            </span>
          </div>
        ) : (
          <div>
            {recent.map((t) => {
              const pnl = Number(t.pnl);
              const isWin = pnl > 0;
              return (
                <div
                  key={t.id}
                  className="flex items-center justify-between px-4 py-3"
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                >
                  <div className="flex items-center gap-2.5">
                    <span className="font-mono text-sm font-bold" style={{ color: "rgba(255,255,255,0.85)" }}>
                      {t.instrument}
                    </span>
                    <span
                      className="text-[9px] font-bold uppercase tracking-[0.06em] px-1.5 py-0.5"
                      style={{
                        color: t.direction === "long" ? "#1d9e75" : "#ef4444",
                        background: t.direction === "long" ? "rgba(29,158,117,0.10)" : "rgba(239,68,68,0.10)",
                      }}
                    >
                      {t.direction === "long" ? "L" : "S"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[10px]" style={{ color: "rgba(255,255,255,0.3)" }}>
                      {t.date?.slice(5)}
                    </span>
                    <span
                      className="font-mono text-sm font-bold tabular-nums"
                      style={{ color: isWin ? "#1d9e75" : "#ef4444" }}
                    >
                      {isWin ? "+" : "-"}${Math.abs(pnl).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Desktop table */}
      <div className="flex-1 overflow-x-auto hidden lg:block">
        {recent.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <span className="text-xs uppercase tracking-[0.08em]" style={{ color: "rgba(255,255,255,0.25)" }}>
              No trades yet
            </span>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                {["Time", "Symbol", "Side", "P&L"].map((h) => (
                  <th
                    key={h}
                    className={cn(
                      "px-4 py-2 font-medium uppercase tracking-[0.08em]",
                      h === "P&L" ? "text-right" : "text-left"
                    )}
                    style={{ color: "rgba(255,255,255,0.3)" }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recent.map((t) => {
                const pnl = Number(t.pnl);
                const isWin = pnl > 0;
                return (
                  <tr
                    key={t.id}
                    className="transition-colors hover:bg-white/[0.02]"
                    style={{ borderBottom: "1px solid rgba(255,255,255,0.03)" }}
                  >
                    <td className="px-4 py-2.5 font-mono" style={{ color: "rgba(255,255,255,0.3)" }}>
                      {t.date?.slice(5)}
                    </td>
                    <td className="px-4 py-2.5 font-mono" style={{ color: "rgba(255,255,255,0.8)" }}>
                      {t.instrument}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className="text-[9px] font-bold uppercase tracking-[0.06em] px-1.5 py-0.5"
                        style={{
                          color: t.direction === "long" ? "#1d9e75" : "#ef4444",
                          background: t.direction === "long" ? "rgba(29,158,117,0.10)" : "rgba(239,68,68,0.10)",
                        }}
                      >
                        {t.direction === "long" ? "LONG" : "SHORT"}
                      </span>
                    </td>
                    <td
                      className="px-4 py-2.5 text-right font-mono tabular-nums"
                      style={{ color: isWin ? "#1d9e75" : "#ef4444" }}
                    >
                      {isWin ? "+" : "-"}${Math.abs(pnl).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <div className="px-4 py-2" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        <NavLink
          to="/journal"
          className="text-[10px] uppercase tracking-[0.08em] transition-colors"
          style={{ color: "rgba(255,255,255,0.3)" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.6)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.3)")}
        >
          View all 
        </NavLink>
      </div>
    </div>
  );
}

// â"€â"€â"€ Risk & Discipline Panel â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

interface RiskMetric {
  label: string;
  value: string;
  pct?: number;
  alert?: boolean;
}

function RiskPanel({ metrics }: { metrics: RiskMetric[] }) {
  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <span
          className="text-[10px] font-medium uppercase tracking-[0.1em]"
          style={{ color: "rgba(255,255,255,0.4)" }}
        >
          Risk &amp; Discipline
        </span>
      </div>
      <div className="flex-1 flex flex-col justify-around px-4 py-4 gap-4">
        {metrics.map((m) => (
          <div key={m.label} className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span
                className="text-[10px] uppercase tracking-[0.08em]"
                style={{ color: "rgba(255,255,255,0.4)" }}
              >
                {m.label}
              </span>
              <span
                className="font-mono text-sm font-bold tabular-nums"
                style={{ color: m.alert ? "#ef4444" : m.pct !== undefined ? "#1d9e75" : "rgba(255,255,255,0.9)" }}
              >
                {m.value}
              </span>
            </div>
            {m.pct !== undefined && (
              <div
                className="w-full h-1"
                style={{ background: "rgba(255,255,255,0.06)" }}
              >
                <div
                  className="h-full transition-all duration-500"
                  style={{
                    width: `${Math.min(100, m.pct)}%`,
                    background: m.alert ? "#ef4444" : "#1d9e75",
                  }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// â"€â"€â"€ Main Export â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

export default function Dashboard() {
  const { user, checkSubscription } = useAuth();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [timeRange, setTimeRange] = useState<TimeRange>("all");
  const [excludeEval, setExcludeEval] = useState(true);
  useRealtimeDashboard();

  usePageMeta({
    title: "Dashboard",
    description: "Your trading performance overview -- track P&L, win rate, equity curve and risk metrics in real-time.",
  });

  // Post-checkout upgrade toast -- with retries for webhook propagation
  useEffect(() => {
    if (searchParams.get("upgraded") !== "true") return;
    const tier = searchParams.get("tier") || "pro";
    const tierLabel = tier === "elite" ? "ELITE" : "PRO";
    trackSubscribe(tier, tier === "elite" ? 39 : 15); // ad conversion signal (monthly £)
    setSearchParams({}, { replace: true });

    const showToast = () =>
      toast.success(`${tierLabel} ACCESS GRANTED`, {
        description: `Your account has been upgraded to ${tierLabel}. All ${tierLabel} features are now unlocked.`,
        duration: 8000,
        style: {
          background: "#000",
          border: "1px solid #1d9e75",
          color: "#1d9e75",
          fontFamily: "monospace",
          fontSize: "13px",
          letterSpacing: "0.05em",
        },
      });

    checkSubscription().then(showToast);
    const t1 = setTimeout(() => checkSubscription(), 2000);
    const t2 = setTimeout(() => checkSubscription(), 6000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const d = useDashboardData(timeRange, excludeEval);

  const handleExport = useCallback(() => {
    const trades = getTrades();
    if (!trades.length) { toast.info("No trades to export"); return; }
    const headers = ["Date","Time","Instrument","Direction","Entry Price","Exit Price","Stop Loss","Take Profit","R:R","P&L","P&L %","Lot Size","Strategy","Session","Emotion","Notes","Tags"];
    const rows = trades.map((t) => [
      t.date, t.time, t.instrument, t.direction,
      t.entryPrice, t.exitPrice, t.stopLoss, t.takeProfit,
      t.rr, t.pnl, t.pnlPercent, t.lotSize,
      t.strategy, t.session, t.emotion,
      `"${(t.notes ?? "").replace(/"/g, '""')}"`,
      `"${(t.tags ?? []).join(", ")}"`,
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `marginmetrics-trades-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${trades.length} trades`);
  }, []);

  // Active prop challenge -- drives KPI override when present
  const { data: activeChallenge = null } = useQuery({
    queryKey: ["active-prop-challenge", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await (supabase as any)
        .from("prop_challenges")
        .select("id,firm,account_size,current_balance,current_pnl,peak_balance,status,profit_target,daily_loss_limit,max_loss_limit,daily_pnl,trading_days,started_at")
        .eq("user_id", user.id)
        .eq("status", "active")
        .order("started_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return data ?? null;
    },
    enabled: !!user?.id,
  });

  // Challenge equity curve -- trades from challenge start date, based on challenge account size
  const { data: challengeEquityData } = useQuery({
    queryKey: ["challenge-equity", user?.id, (activeChallenge as any)?.id],
    queryFn: async () => {
      if (!user?.id || !activeChallenge) return null;
      const ac = activeChallenge as any;
      const startDate = ac.started_at ? (ac.started_at as string).slice(0, 10) : null;
      if (!startDate) return null;
      const { data, error } = await supabase
        .from("trades")
        .select("id,date,time,pnl,created_at")
        .eq("user_id", user.id)
        .gte("date", startDate)
        .order("date", { ascending: true });
      if (error) return null;
      const accountSize = Number(ac.account_size);
      if (!data || data.length === 0) {
        return { curve: [], startingBalance: accountSize, currentBalance: accountSize, currentPnl: 0, dailyPnl: 0, tradingDays: 0 };
      }
      const today = new Date().toISOString().slice(0, 10);
      let balance = accountSize;
      let peak = balance;
      let currentPnl = 0;
      let dailyPnl = 0;
      const days = new Set<string>();
      const curve = (data as { id: string; date: string; time: string | null; pnl: number; created_at: string }[]).map((t) => {
        const pnl = Number(t.pnl);
        balance += pnl;
        currentPnl += pnl;
        if ((t.date ?? "").slice(0, 10) === today) dailyPnl += pnl;
        if (t.date) days.add(String(t.date).slice(0, 10));
        peak = Math.max(peak, balance);
        const dd = peak > 0 ? ((peak - balance) / peak) * 100 : 0;
        const ts = t.time
          ? new Date(`${t.date}T${t.time.length === 5 ? t.time + ":00" : t.time}`)
          : new Date(t.created_at);
        return {
          date: ts.toISOString(),
          label: ts.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
          balance: Math.round(balance * 100) / 100,
          equity: Math.round(balance * 100) / 100,
          drawdown: Math.round(dd * 100) / 100,
        };
      });
      return { curve, startingBalance: accountSize, currentBalance: Math.round(balance * 100) / 100, currentPnl: Math.round(currentPnl * 100) / 100, dailyPnl: Math.round(dailyPnl * 100) / 100, tradingDays: days.size };
    },
    enabled: !!user?.id && !!activeChallenge,
  });

  // Raw trades for bottom row
  const { data: rawTrades = [] } = useQuery({
    queryKey: ["dashboard-raw-trades", user?.id, timeRange],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("trades")
        .select("id, instrument, direction, pnl, date, session")
        .eq("user_id", user.id)
        .order("date", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data || []) as RawTrade[];
    },
    enabled: !!user?.id,
  });

  // Live balance from a connected broker (Tradovate/Lucid etc.) — when present it
  // becomes the headline balance instead of the journal-derived figure.
  const { data: liveBalance } = useQuery({
    queryKey: ["dashboard-live-balance", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from("broker_connections_safe" as any)
        .select("metadata, last_synced_at")
        .eq("status", "connected")
        .order("last_synced_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      const bal = (data as any)?.metadata?.balance;
      return typeof bal === "number" ? bal : null;
    },
    enabled: !!user?.id,
    refetchInterval: 60_000,
  });

  const handleRefresh = useCallback(async () => { await d.refetch?.(); }, [d]);
  void handleRefresh; // suppress unused warning

  if (d.isLoading) {
    return (
      <DashboardLayout>
        <DashboardSkeleton />
      </DashboardLayout>
    );
  }

  // Derived values for KPI strip
  const journalBalance = d.equityCurve.length > 0
    ? d.equityCurve[d.equityCurve.length - 1].balance
    : d.startingBalance + d.totalPnl;
  // Connected broker balance is the source of truth when available (user opted to
  // replace the headline balance with the live account balance).
  const currentBalance = typeof liveBalance === "number" ? liveBalance : journalBalance;
  const currentEquity = d.equityCurve.length > 0
    ? d.equityCurve[d.equityCurve.length - 1].equity
    : currentBalance;

  const fmtPnl = (v: number) => `${v >= 0 ? "+" : ""}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const fmtDollar = (v: number) => `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  const pnlSentiment = (v: number): "positive" | "negative" | "neutral" =>
    v > 0 ? "positive" : v < 0 ? "negative" : "neutral";

  // Best single-day session P&L from recent trades
  const bestSessionPnl = (() => {
    const byDate: Record<string, number> = {};
    rawTrades.forEach((t) => {
      const key = t.date?.slice(0, 10) ?? "unknown";
      byDate[key] = (byDate[key] ?? 0) + Number(t.pnl);
    });
    const vals = Object.values(byDate);
    return vals.length > 0 ? Math.max(...vals) : 0;
  })();

  const challengeKpis: KPIItem[] | null = activeChallenge ? (() => {
    const ac = activeChallenge as any;
    // Prefer live date-based stats (same source as the equity curve) over the stored
    // columns on prop_challenges — those columns aren't updated when a journal trade is
    // logged, so they read $0 even when the equity curve shows profit.
    const liveBalance = challengeEquityData?.currentBalance ?? Number(ac.current_balance);
    const livePnl     = challengeEquityData?.currentPnl ?? Number(ac.current_pnl);
    const liveDaily   = challengeEquityData?.dailyPnl ?? Number(ac.daily_pnl ?? 0);
    const liveDays    = challengeEquityData?.tradingDays ?? ac.trading_days;
    const targetAmt = ac.account_size * (ac.profit_target / 100);
    const maxLossAmt = ac.account_size * (ac.max_loss_limit / 100);
    const drawdownUsed = maxLossAmt > 0 ? Math.max(0, ((ac.account_size - liveBalance) / maxLossAmt * 100)) : 0;
    const targetPct = targetAmt > 0 ? ((Math.max(0, livePnl) / targetAmt) * 100).toFixed(1) : "0.0";
    return [
      { label: "Challenge Balance", value: fmtDollar(liveBalance), mobileValue: compactDollar(liveBalance), sentiment: "neutral" as const },
      { label: "Challenge P&L",     value: fmtPnl(livePnl),        mobileValue: compactPnl(livePnl),        sentiment: pnlSentiment(livePnl) },
      { label: "Today P&L",         value: fmtPnl(liveDaily),      mobileValue: compactPnl(liveDaily),      sentiment: pnlSentiment(liveDaily) },
      { label: "Target %",          value: `${targetPct}%`,                sentiment: Number(targetPct) >= 50 ? "positive" as const : "neutral" as const },
      { label: "Day",               value: `${liveDays}`,          sentiment: "neutral" as const },
      { label: "DD Used",           value: `${drawdownUsed.toFixed(1)}%`,  sentiment: drawdownUsed > 80 ? "negative" as const : "neutral" as const },
      { label: "Win Rate",          value: `${d.winRate}%`,                sentiment: d.winRate >= 50 ? "positive" as const : "negative" as const },
    ];
  })() : null;

  const kpis: KPIItem[] = challengeKpis ?? [
    { label: "Today P&L",    value: fmtPnl(d.todayPnl),      mobileValue: compactPnl(d.todayPnl),      sentiment: pnlSentiment(d.todayPnl) },
    { label: "Win Rate",     value: `${d.winRate}%`,          sentiment: d.winRate >= 50 ? "positive" : "negative" },
    { label: "Balance",      value: fmtDollar(currentBalance), mobileValue: compactDollar(currentBalance), sentiment: "neutral" },
    { label: "Total P&L",   value: fmtPnl(d.totalPnl),       mobileValue: compactPnl(d.totalPnl),       sentiment: pnlSentiment(d.totalPnl) },
    { label: "Trades",       value: `${d.tradeCount}`,        sentiment: "neutral", desktopOnly: false },
    { label: "Best Session", value: fmtPnl(bestSessionPnl),   mobileValue: compactPnl(bestSessionPnl),   sentiment: pnlSentiment(bestSessionPnl), desktopOnly: true },
    { label: "Equity",       value: fmtDollar(currentEquity), mobileValue: compactDollar(currentEquity),  sentiment: "neutral", desktopOnly: true },
  ];

  // Risk & Discipline metrics
  const violationsCount = rawTrades.filter((t) => Number(t.pnl) < 0).length; // proxy until violations table
  const disciplineScore = d.tradeCount > 0
    ? Math.max(0, Math.round(100 - (d.maxDD * 2) - (violationsCount / d.tradeCount * 20)))
    : 100;

  const riskMetrics: RiskMetric[] = [
    {
      label: "Avg Risk / Trade",
      value: `${d.avgRiskPerTrade.toFixed(2)}%`,
      pct: Math.min(100, d.avgRiskPerTrade * 50),
    },
    {
      label: "Rule Violations",
      value: `${d.violationsToday}`,
      alert: d.violationsToday > 0,
    },
    // DD only shown when a prop challenge is active — otherwise show Avg R:R
    activeChallenge ? {
      label: "Max Drawdown",
      value: `${d.maxDD.toFixed(1)}%`,
      pct: Math.min(100, d.maxDD * 5),
      alert: d.maxDD > 10,
    } : {
      label: "Avg R:R",
      value: d.tradeCount > 0 ? `${d.avgRR.toFixed(2)}R` : "--",
      pct: Math.min(100, d.avgRR * 33),
    },
    {
      label: "Discipline Score",
      value: `${disciplineScore}%`,
      pct: disciplineScore,
    },
  ];

  //  Mobile: purpose-built lite layout 
  const equityCurveData = (activeChallenge && challengeEquityData?.curve?.length)
    ? challengeEquityData.curve
    : d.equityCurve;
  const equityStartingBalance = (activeChallenge && challengeEquityData)
    ? challengeEquityData.startingBalance
    : d.startingBalance;

  if (isMobile) {
    return (
      <DashboardLayout>
        {!d.hasTrades && <WelcomeModal />}
        <MobileDashboard
          d={d}
          rawTrades={rawTrades}
          currentBalance={currentBalance}
          equityCurve={equityCurveData}
          startingBalance={equityStartingBalance}
          timeRange={timeRange}
          setTimeRange={setTimeRange}
          excludeEval={excludeEval}
          setExcludeEval={setExcludeEval}
          activeChallenge={activeChallenge}
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {!d.hasTrades && <WelcomeModal />}

      <div className="flex flex-col gap-4 animate-fade-in min-w-0">

        {/* â"€â"€ Header bar â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
        <div className="flex items-center justify-end gap-2 flex-wrap">
          {/* Time range toggle */}
          <div
            className="flex items-center"
            style={{ border: "1px solid rgba(255,255,255,0.06)" }}
          >
            {TIME_RANGES.map((r) => (
              <button
                key={r.value}
                onClick={() => setTimeRange(r.value)}
                className="transition-colors"
                style={{
                  padding: "6px 12px",
                  fontSize: "10px",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  borderRight: "1px solid rgba(255,255,255,0.06)",
                  background: timeRange === r.value ? "rgba(255,255,255,0.08)" : "transparent",
                  color: timeRange === r.value ? "white" : "rgba(255,255,255,0.35)",
                }}
                onMouseEnter={(e) => {
                  if (timeRange !== r.value) e.currentTarget.style.color = "rgba(255,255,255,0.6)";
                }}
                onMouseLeave={(e) => {
                  if (timeRange !== r.value) e.currentTarget.style.color = "rgba(255,255,255,0.35)";
                }}
              >
                {r.label}
              </button>
            ))}
          </div>

          <button
            onClick={() => setExcludeEval((v) => !v)}
            style={{
              padding: "6px 12px",
              fontSize: "10px",
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              border: "1px solid rgba(255,255,255,0.06)",
              color: excludeEval ? "#1d9e75" : "rgba(255,255,255,0.35)",
              background: excludeEval ? "rgba(29,158,117,0.08)" : "transparent",
            }}
          >
            {excludeEval ? "Live Only" : "All Trades"}
          </button>

          <button
            style={{
              padding: "6px 12px",
              fontSize: "10px",
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              border: "1px solid rgba(255,255,255,0.06)",
              color: "rgba(255,255,255,0.35)",
              background: "transparent",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.6)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.35)")}
            onClick={handleExport}
          >
            Export
          </button>
        </div>

        {/* â"€â"€ Active prop challenge banner â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
        {activeChallenge && (
          <Link
            to="/prop-challenge"
            className="flex items-center justify-between gap-3 px-4 py-2.5 transition-opacity hover:opacity-80"
            style={{
              background: "rgba(29,158,117,0.08)",
              border: "1px solid rgba(29,158,117,0.3)",
            }}
          >
            <div className="flex items-center gap-2.5">
              <Trophy size={14} strokeWidth={1.5} style={{ color: "#1d9e75", flexShrink: 0 }} />
              <span
                className="text-[10px] font-mono font-bold uppercase tracking-[0.08em]"
                style={{ color: "#1d9e75" }}
              >
                Prop Challenge Active
              </span>
              <span className="text-[10px] font-mono" style={{ color: "rgba(255,255,255,0.45)" }}>
                {(activeChallenge as any).firm?.toUpperCase()} {"·"} ${(activeChallenge as any).account_size?.toLocaleString()} {"·"} Day {(activeChallenge as any).trading_days}
              </span>
            </div>
            <span className="text-[9px] font-mono uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.3)" }}>
              KPIs reflect challenge account 
            </span>
          </Link>
        )}

        {/* â"€â"€ KPI strip â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
        <KPIStrip items={kpis} />
        {excludeEval && d.evalTradeCount > 0 && (
          <p
            style={{
              fontSize: 9,
              fontFamily: "monospace",
              color: "rgba(245,158,11,0.5)",
              letterSpacing: "0.06em",
              marginTop: -8,
            }}
          >
            {d.evalTradeCount} eval trade{d.evalTradeCount !== 1 ? "s" : ""} excluded from stats
          </p>
        )}

        {/* â"€â"€ Empty state â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
        {!d.hasTrades && <EmptyStateCTA />}

        {d.hasTrades && (
          <>
            {/* â"€â"€ Equity curve hero â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
            <div
              className="h-[200px] sm:h-[280px]"
              style={{
                border: "1px solid rgba(255,255,255,0.06)",
                overflow: "hidden",
              }}
            >
              {/* When challenge active, use challenge-period trades starting from account_size */}
              {activeChallenge && challengeEquityData?.curve && challengeEquityData.curve.length > 0 ? (
                <EnhancedEquityCurve
                  data={challengeEquityData.curve}
                  startingBalance={challengeEquityData.startingBalance}
                />
              ) : !activeChallenge && d.equityCurve.length > 0 ? (
                <EnhancedEquityCurve
                  data={d.equityCurve}
                  startingBalance={d.startingBalance}
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <span
                    className="text-xs uppercase tracking-[0.08em]"
                    style={{ color: "rgba(255,255,255,0.25)" }}
                  >
                    {activeChallenge ? "No trades logged since challenge started" : "Log trades to see equity curve"}
                  </span>
                </div>
              )}
            </div>

            {/* â"€â"€ Bottom row â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
            <div
              className="grid grid-cols-1 lg:grid-cols-[65fr_35fr]"
              style={{
                border: "1px solid rgba(255,255,255,0.06)",
                minHeight: 280,
                alignItems: "stretch",
              }}
            >
              {/* Recent trades -- 65% */}
              <div style={{ borderRight: "1px solid rgba(255,255,255,0.06)" }}>
                <RecentTradesTable trades={rawTrades} />
              </div>

              {/* Risk & Discipline -- 35% */}
              <RiskPanel metrics={riskMetrics} />
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
